import React, { useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loading } from "modules/_shared/pages";
import { useGetAllSpecAuditSessionsQuery } from "../../data/api/queries/knowledgeAgent";
import { SpecAuditSessionResponse } from "../../data/models/knowledge-agent.model";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import { SmartToyOutlined } from "@mui/icons-material";
import "./AgentAllDetails.scss";

const AgentAllDetails: React.FC = () => {
  const navigate = useNavigate();

  const {
    data: sessionsData,
    isLoading,
    isFetching
  } = useGetAllSpecAuditSessionsQuery();

  const getTimeAgo = (dateStr: string): string => {
    const now = new Date();
    const normalized = dateStr.match(/[Zz]|[+-]\d{2}:\d{2}$/)
      ? dateStr
      : dateStr + "Z";
    const date = new Date(normalized);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const getInitials = (name: string): string => {
    return name
      .split(".")
      .filter((_, i, arr) => i === 0 || i === arr.length - 1)
      .map((s) => s.charAt(0).toUpperCase())
      .join("");
  };

  const getStatusConfig = (status: string) => {
    const s = status.toLowerCase();
    if (s === "complete")
      return {
        icon: <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />,
        label: "Complete",
        className: "status-complete"
      };
    if (s === "running")
      return {
        icon: <CircularProgress size={14} thickness={5} color="inherit" />,
        label: "Running",
        className: "status-running"
      };
    if (s === "hitl_required")
      return {
        icon: <PanToolOutlinedIcon sx={{ fontSize: 16 }} />,
        label: "Action Required",
        className: "status-hitl"
      };
    if (s === "error")
      return {
        icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
        label: "Error",
        className: "status-error"
      };
    return {
      icon: <CircularProgress size={14} thickness={5} color="inherit" />,
      label: status,
      className: "status-default"
    };
  };

  const handleCardClick = useCallback(
    (session: SpecAuditSessionResponse) => {
      navigate(`/knowledge-agent/${session.sessionId}`);
    },
    [navigate]
  );

  const handleNewAgent = useCallback(() => {
    navigate("/knowledge-agent/create-session");
  }, [navigate]);

  if (isLoading && !sessionsData) {
    return (
      <div className="agent-all-details-container">
        <nav className="agent-breadcrumb">
          <Link to="/home" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">Knowledge Agents</span>
        </nav>
        <div className="agent-page-header">
          <h1 className="agent-page-title">Knowledge Agents</h1>
          <p className="agent-page-description">
            Compare engineering specifications against standards and identify
            compliance gaps using AI.
          </p>
        </div>
        <div className="agent-all-details-content">
          <div className="loading-container">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  const sessions: SpecAuditSessionResponse[] = sessionsData ?? [];

  return (
    <div className="agent-all-details-container">
      <nav className="agent-breadcrumb">
        <Link to="/home" className="breadcrumb-link">
          Home
        </Link>
        <span className="breadcrumb-separator">&gt;</span>
        <span className="breadcrumb-current">Knowledge Agents</span>
      </nav>

      <div className="agent-page-header">
        <h1 className="agent-page-title">Knowledge Agents</h1>
        <p className="agent-page-description">
          Compare engineering specifications against standards and identify
          compliance gaps using AI.
        </p>
      </div>

      <div className="agent-sessions-section">
        <div className="sessions-header">
          <h2>Available Knowledge Agents</h2>
          <button className="new-agent-btn" onClick={handleNewAgent}>
            <svg
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Knowledge Agent
          </button>
        </div>

        {isFetching && sessions.length > 0 && (
          <div className="fetching-indicator">
            <CircularProgress size={16} />
            <span>Refreshing...</span>
          </div>
        )}

        {sessions.length > 0 && (
          <div className="sessions-grid">
            {sessions.map((session) => {
              const statusConfig = getStatusConfig(session.status);
              return (
                <div
                  key={session.queryId}
                  className="session-card"
                  // onClick={() => handleCardClick(session)}
                >
                  <div className="session-card__top">
                    <div
                      className={`session-card__status ${statusConfig.className}`}
                    >
                      {statusConfig.icon}
                      <span>{statusConfig.label}</span>
                    </div>
                    <span className="session-card__time">
                      <AccessTimeIcon sx={{ fontSize: 14 }} />
                      {getTimeAgo(session.createdAt)}
                    </span>
                  </div>

                  {/* Query Section */}
                  <div className="session-card__query">
                    <QuestionAnswerOutlinedIcon sx={{ fontSize: 18 }} />
                    <p title={session.userQuery}>
                      {session.userQuery || "No query specified"}
                    </p>
                  </div>

                  {/* Name and date section */}
                  {/* <div className="session-card__footer">
                    <div className="session-card__user">
                      <div className="user-avatar">
                        {getInitials(session.createdBy || "System")}
                      </div>
                      <span className="user-name">
                        {(session.createdBy || "System")
                          .split(".")
                          .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
                          .join(" ")}
                      </span>
                    </div>
                    <span className="session-card__id">
                      {new Date(session.createdAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric"
                      })}
                    </span>
                  </div> */}

                  {/* Button Section  */}
                  <button
                    className={`session-card__action-btn ${
                      session.status.toLowerCase() === "complete"
                        ? "action-btn--complete"
                        : session.status.toLowerCase() === "hitl_required"
                        ? "action-btn--hitl"
                        : session.status.toLowerCase() === "error"
                        ? "action-btn--error"
                        : session.status.toLowerCase() === "running"
                        ? "action-btn--running"
                        : "action-btn--default"
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(session);
                    }}
                  >
                    {session.status.toLowerCase() === "complete" &&
                      "View Result"}
                    {session.status.toLowerCase() === "hitl_required" &&
                      "Respond to Agent"}
                    {session.status.toLowerCase() === "error" && "View Error"}
                    {session.status.toLowerCase() === "running" &&
                      "View Progress"}
                    <span className="action-btn__chevron">&rsaquo;</span>
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {sessions.length === 0 && !isFetching && (
          <div className="empty-state">
            <div className="empty-state__icon">
              <SmartToyOutlined sx={{ fontSize: 56, color: "#9ca3af" }} />
            </div>
            <h3 className="empty-state__title">No knowledge agents found</h3>
            <p className="empty-state__subtitle">
              No knowledge agent sessions available.
            </p>
            <button className="empty-state__btn" onClick={handleNewAgent}>
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                width="16"
                height="16"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Your First Knowledge Agent
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentAllDetails;
